import React from "react";
import { useCountdown } from "../../../utilities/timer";

// A small hourglass illustration, hand-drawn in SVG rather than pulled from
// an icon set, so it can carry the gold gradient + glass-sand detailing the
// "expired" banner wants. Purely decorative — aria-hidden.
const HourglassIllustration = () => (
  <svg
    className="goldenAwardsCountdown-hourglassSvg"
    viewBox="0 0 48 48"
    fill="none"
    aria-hidden="true"
  >
    <defs>
      <linearGradient id="hourglassGold" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#fff3c4" />
        <stop offset="100%" stopColor="#ffcc00" />
      </linearGradient>
    </defs>
    <rect x="9" y="5" width="30" height="4" rx="2" fill="url(#hourglassGold)" />
    <rect x="9" y="39" width="30" height="4" rx="2" fill="url(#hourglassGold)" />
    <path
      d="M13 9 H35 L25.2 24 L35 39 H13 L22.8 24 Z"
      fill="rgba(255,255,255,0.08)"
      stroke="url(#hourglassGold)"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    {/* Top pile drains downward into the neck; bottom pile fills upward
        from the base — kept as two independently animated shapes so the
        sand always reads as falling, never rising. */}
    <path
      className="goldenAwardsCountdown-hourglassSandTop"
      d="M16.5 12 H31.5 L24.4 22.2 H23.6 Z"
      fill="url(#hourglassGold)"
      opacity="0.9"
    />
    <circle
      className="goldenAwardsCountdown-hourglassStream"
      cx="24"
      cy="23"
      r="1"
      fill="url(#hourglassGold)"
    />
    <path
      className="goldenAwardsCountdown-hourglassSandBottom"
      d="M23.6 25.8 H24.4 L29 36 H19 Z"
      fill="url(#hourglassGold)"
      opacity="0.85"
    />
  </svg>
);

// A soccer ball, hand-drawn in SVG to match the hourglass's gold-gradient
// treatment rather than pulled from an icon set — the pentagon patches are
// picked out in the dark navy background color so it still reads as a
// football at this size. Purely decorative — aria-hidden.

const FootballIllustration = () => (
  <svg
    className="goldenAwardsCountdown-footballSvg"
    viewBox="0 0 48 48"
    fill="none"
    aria-hidden="true"
  >
    <defs>
      <radialGradient id="footballGold" cx="35%" cy="30%" r="75%">
        <stop offset="0%" stopColor="#fff3c4" />
        <stop offset="100%" stopColor="#ffcc00" />
      </radialGradient>
      <clipPath id="footballBallClip">
        <circle cx="24" cy="24" r="17" />
      </clipPath>
    </defs>

    {/* Ball base */}
    <circle cx="24" cy="24" r="17" fill="url(#footballGold)" />
    <circle cx="24" cy="24" r="17" fill="none" stroke="#071d49" strokeWidth="1" opacity="0.2" />

    <g clipPath="url(#footballBallClip)">
      {/* Seams connecting the center pentagon to the five edge patches */}
      <g stroke="#071d49" strokeWidth="1.2" strokeLinecap="round" opacity="0.85">
        <line x1="24" y1="17" x2="24" y2="5" />
        <line x1="30.66" y1="21.84" x2="42.07" y2="18.13" />
        <line x1="28.12" y1="29.66" x2="35.17" y2="39.37" />
        <line x1="19.88" y1="29.66" x2="12.83" y2="39.37" />
        <line x1="17.34" y1="21.84" x2="5.93" y2="18.13" />
      </g>

      {/* Five edge patches, positioned so only their inner arc peeks
          into the ball, matching a real football's rim patches */}
      <g fill="#071d49">
        <circle cx="24" cy="5" r="6" />
        <circle cx="42.07" cy="18.13" r="6" />
        <circle cx="35.17" cy="39.37" r="6" />
        <circle cx="12.83" cy="39.37" r="6" />
        <circle cx="5.93" cy="18.13" r="6" />
      </g>

      {/* Center pentagon */}
      <path
        d="M24 17 L30.66 21.84 L28.12 29.66 L19.88 29.66 L17.34 21.84 Z"
        fill="#071d49"
      />
    </g>
  </svg>
);

// isExpired/winnersAnnounced are both driven by the backend's own flags
// (see getQuestionState in GoldenAwardQuestion.js) rather than recomputed
// here from the deadline, since the API already tells us the question set's
// state. winnersAnnounced takes priority — once results are out, the "time's
// up" hourglass is stale news.
const GoldenAwardCountdown = ({ deadline, isExpired, winnersAnnounced }) => {
  const { days, hours, minutes, seconds } = useCountdown(deadline);

  if (winnersAnnounced) {
    return (
      <div className="goldenAwardsCountdown goldenAwardsCountdown-announced">
        <span className="goldenAwardsCountdown-sparkle goldenAwardsCountdown-sparkle-1" />
        <span className="goldenAwardsCountdown-sparkle goldenAwardsCountdown-sparkle-2" />
        <span className="goldenAwardsCountdown-sparkle goldenAwardsCountdown-sparkle-3" />
        <div className="goldenAwardsCountdown-announcedBadge">
          <FootballIllustration />
        </div>
        <div className="goldenAwardsCountdown-announcedText">
          <p className="goldenAwardsCountdown-label goldenAwardsCountdown-announcedTitle">
            {localStorage.language == "en" ? "Winners Announced!" : "വിജയികളെ പ്രഖ്യാപിച്ചു!"}
          </p>
          <p className="goldenAwardsCountdown-note">
            {localStorage.language == "en"
              ? "Congratulations to all the winners! Scroll down to see the results."
              : "എല്ലാ വിജയികൾക്കും അഭിനന്ദനങ്ങൾ! ഫലങ്ങൾ കാണാൻ താഴേക്ക് സ്ക്രോൾ ചെയ്യുക."}
          </p>
        </div>
      </div>
    );
  }

  if (isExpired) {
    return (
      <div className="goldenAwardsCountdown goldenAwardsCountdown-expired">
        <span className="goldenAwardsCountdown-sparkle goldenAwardsCountdown-sparkle-1" />
        <span className="goldenAwardsCountdown-sparkle goldenAwardsCountdown-sparkle-2" />
        <span className="goldenAwardsCountdown-sparkle goldenAwardsCountdown-sparkle-3" />
        <div className="goldenAwardsCountdown-expiredBadge">
          <HourglassIllustration />
        </div>
        <div className="goldenAwardsCountdown-expiredText">
          <p className="goldenAwardsCountdown-label goldenAwardsCountdown-expiredTitle">
            {localStorage.language == "en" ? "Time's Up!" : "സമയം കഴിഞ്ഞു!"}
          </p>
          <p className="goldenAwardsCountdown-note">
            {localStorage.language == "en"
              ? "The winner of each question will be announced after the final match."
              : "ഓരോ ചോദ്യത്തിന്റെയും വിജയികളെ അവസാന മത്സരത്തിനു ശേഷം പ്രഖ്യാപിക്കും."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="goldenAwardsCountdown">
      <p className="goldenAwardsCountdown-label">
        {localStorage.language == "en" ? "Predictions closes in" : "പ്രവചനങ്ങൾ അവസാനിക്കാൻ ബാക്കി"}
      </p>
      <h6 className="goldenAwardsCountdown-time">
        {days === 0
          ? hours + ":" + minutes + ":" + seconds
          : days > 1
            ? days +
              (localStorage.language == "en" ? " days " : " ദിവസം ") +
              hours + ":" + minutes + ":" + seconds
            : days +
              (localStorage.language == "en" ? " day  " : " ദിവസം ") +
              hours + ":" + minutes + ":" + seconds}
      </h6>
    </div>
  );
};

export default GoldenAwardCountdown;
